import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { FormsModule } from "@angular/forms";
import { PaymentsStore } from "./s25.payments.store";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25PricingUtil } from "../../s25.pricing.util";
import { Bind } from "../../../../decorators/bind.decorator";

const REASON_LIMIT = 64;

@TypeManagerDecorator("s25-ng-void-payment-form")
@Component({
    selector: "s25-ng-void-payment-form",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, S25ButtonComponent],
    template: `
        <p class="void-prompt">
            Void the {{ amountLabel() }} payment <b>#{{ paymentDetailId() }}</b
            >? This cannot be undone.
        </p>

        <label class="void-reason">
            <span class="void-reason--label">
                Reason <span aria-hidden="true">*</span>
                <span class="void-reason--count">{{ reason().length }} / {{ reasonLimit }}</span>
            </span>
            <input
                class="c-input"
                type="text"
                required
                [maxlength]="reasonLimit"
                [ngModel]="reason()"
                (ngModelChange)="reason.set($event)"
            />
        </label>

        @if (error()) {
            <p class="void-error" role="alert">{{ error() }}</p>
        }

        <div class="void-buttons">
            <s25-ng-button [type]="'danger'" [disabled]="!reason().trim()" [onClick]="confirm"
                >Void Payment</s25-ng-button
            >
            <s25-ng-button [type]="'outline'" (clicked)="cancelled.emit()">Cancel</s25-ng-button>
        </div>
    `,
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            gap: 8px;
            padding: 0.75rem 1rem;
        }

        .void-reason {
            display: flex;
            flex-direction: column;
            gap: 2px;
            max-width: 30em;
        }

        .void-reason--label {
            display: flex;
            justify-content: space-between;
            gap: 8px;
            font-weight: 600;
        }

        .void-reason--count {
            font-weight: 400;
            color: var(--color--text-muted);
        }

        .void-error {
            color: var(--color--text-danger);
        }

        .void-buttons {
            display: flex;
            gap: 8px;
        }
    `,
})
export class S25VoidPaymentFormComponent {
    private readonly paymentsStore = inject(PaymentsStore);

    readonly paymentDetailId = input.required<number>();
    readonly amountTotalCents = input(0);

    readonly cancelled = output<void>();
    readonly voided = output<void>();

    protected readonly reasonLimit = REASON_LIMIT;
    protected reason = signal("");
    protected error = signal("");
    protected readonly amountLabel = computed(() =>
        S25PricingUtil.formatCurrency((this.amountTotalCents() ?? 0) / 100),
    );

    @Bind
    protected async confirm() {
        const reason = this.reason().trim();
        if (!reason) return;

        const { error } = await this.paymentsStore.voidPayment(this.paymentDetailId(), reason);
        if (error) return this.error.set(error);

        this.error.set("");
        this.voided.emit();
    }
}
